import { useState } from "react"
import { extent, max } from "d3-array"
import { scaleLinear } from "d3-scale"
import { area, line } from "d3-shape"
import { observer } from "mobx-react-lite"
import { format } from "date-fns"

import type { IEpochStore } from "@/domains/epochs/interfaces"

const colors = [
  "#1A56CD",
  "#5A7CFF",
  "#9CA8FF",
  "#38C4B8",
  "#DB14A9",
  "#FACA87",
  "#F0626E",
  "#09D993",
  "#DB1D2C",
  "#CF7F0E",
]

const FRESH_COLOR = colors[0]
const RECYCLED_COLOR = colors[3]
const BPG_COLOR = colors[2]
const TOTAL_EMITTED_COLOR = "#FFFFFF"
const TOTAL_EMITTED_STROKE_WIDTH = 1
const TOTAL_EMITTED_STROKE_DASHARRAY = "1,1"

export interface RecycledEmissionsChartProps {
  historicEpochs: IEpochStore[]
  currentEpoch: IEpochStore | undefined
  hoveredEpoch: number | null
  setHoveredEpoch: (epoch: number | null) => void
  mousePosition: { x: number; y: number } | null
  setMousePosition: (position: { x: number; y: number } | null) => void
}

export const RecycledEmissionsChart = observer(
  ({
    historicEpochs,
    currentEpoch,
    hoveredEpoch,
    setHoveredEpoch,
    mousePosition,
    setMousePosition,
  }: RecycledEmissionsChartProps) => {
    // Combine historic and current epoch for chart display
    // Ensure current epoch is included and not duplicated
    const allEpochsForChart = currentEpoch
      ? [
          ...historicEpochs.filter((epoch) => epoch.epochNumber !== currentEpoch.epochNumber),
          currentEpoch,
        ].sort((a, b) => a.epochNumber - b.epochNumber)
      : historicEpochs.sort((a, b) => a.epochNumber - b.epochNumber)

    // Early return if no data
    if (allEpochsForChart.length === 0) {
      return (
        <div className="w-full aspect-video bg-p400 rounded-lg flex items-center justify-center border border-p400-light">
          <span className="text-t300">No chart data available</span>
        </div>
      )
    }

    // Map epoch data to chart format
    const chartData = allEpochsForChart.map((epoch) => {
      const totalEmitted = Number(epoch.gaugesMint) ?? 0
      const recycled = Number(epoch.recycled) ?? 0
      const fresh = totalEmitted - recycled
      const bpg = 0 // BPG not available in epoch data, set to 0

      return {
        epoch: epoch.epochNumber,
        totalEmitted,
        recycled,
        fresh,
        bpg,
        startTimestamp: epoch.startTimestamp,
      }
    })

    // SVG dimensions
    const svgWidth = 800
    const svgHeight = 450 // 16:9 ratio
    const chartMargin = { top: 64, right: 70, bottom: 40, left: 80 } // More space for percentage labels on right
    const chartWidth = svgWidth - chartMargin.left - chartMargin.right
    const chartHeight = svgHeight - chartMargin.top - chartMargin.bottom

    // D3 Scale functions
    const epochExtent = extent(chartData, (d) => d.epoch) as [number, number]
    const maxStackedValue = max(chartData, (d) => d.totalEmitted + d.bpg) ?? 0

    const xScale = scaleLinear().domain(epochExtent).range([0, chartWidth])
    const yScale = scaleLinear().domain([0, maxStackedValue]).range([chartHeight, 0])

    // D3 Area and Line generators - Recycled first to highlight impact!
    const recycledAreaGenerator = area<(typeof chartData)[0]>()
      .x((d) => xScale(d.epoch))
      .y0(yScale(0)) // Bottom: Start from baseline
      .y1((d) => yScale(d.recycled)) // Top: Go up to recycled value

    const freshAreaGenerator = area<(typeof chartData)[0]>()
      .x((d) => xScale(d.epoch))
      .y0((d) => yScale(d.recycled)) // Bottom: Start from top of recycled
      .y1((d) => yScale(d.recycled + d.fresh)) // Top: Go up to recycled + fresh = totalEmitted

    const bpgAreaGenerator = area<(typeof chartData)[0]>()
      .x((d) => xScale(d.epoch))
      .y0((d) => yScale(d.totalEmitted))
      .y1((d) => yScale(d.totalEmitted + d.bpg))

    const totalEmittedLineGenerator = line<(typeof chartData)[0]>()
      .x((d) => xScale(d.epoch))
      .y((d) => yScale(d.totalEmitted))

    // Generate the actual paths - Order matches new stacking: Recycled → Fresh → BPG
    const recycledAreaPath = recycledAreaGenerator(chartData) ?? ""
    const freshAreaPath = freshAreaGenerator(chartData) ?? ""
    const bpgAreaPath = bpgAreaGenerator(chartData) ?? ""
    const totalEmittedLine = totalEmittedLineGenerator(chartData) ?? ""

    // Generate axis ticks using D3 utilities
    const epochs = chartData.map((d) => d.epoch)
    const xTicks = epochs.filter((_, i) => i % Math.ceil(epochs.length / 8) === 0)
    const yTicks = Array.from({ length: 6 }, (_, i) => Math.round((maxStackedValue / 5) * i))

    const formatEpochDate = (timestamp: number | undefined | null): string => {
      if (timestamp === undefined || timestamp === null || isNaN(Number(timestamp))) {
        return "N/A"
      }
      return format(new Date(Number(timestamp) * 1000), "MMM do yyyy")
    }

    return (
      <div className="relative w-full aspect-video bg-p400 rounded-lg p-4 pt-6 border border-p400-light">
        <svg width="100%" height="100%" viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="overflow-visible">
          <defs>
            <linearGradient id="freshGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={FRESH_COLOR} stopOpacity="0.8" />
              <stop offset="100%" stopColor={FRESH_COLOR} stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="recycledGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={RECYCLED_COLOR} stopOpacity="0.8" />
              <stop offset="100%" stopColor={RECYCLED_COLOR} stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="bpgGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={BPG_COLOR} stopOpacity="0.8" />
              <stop offset="100%" stopColor={BPG_COLOR} stopOpacity="0.4" />
            </linearGradient>
          </defs>

          {/* Chart area */}
          <g transform={`translate(${chartMargin.left}, ${chartMargin.top})`}>
            {/* Grid lines */}
            {yTicks.slice(0, -1).map((tick) => (
              <line
                key={tick}
                x1={0}
                y1={yScale(tick)}
                x2={chartWidth}
                y2={yScale(tick)}
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
            ))}

            {chartData.map((chartItem) => (
              <line
                key={chartItem.epoch}
                x1={xScale(chartItem.epoch)}
                y1={yScale(chartItem.totalEmitted)}
                x2={xScale(chartItem.epoch)}
                y2={yScale(chartHeight)}
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
            ))}

            {/* Recycled area - Now bottom layer to highlight impact! */}
            <path d={recycledAreaPath} fill="url(#recycledGradient)" stroke={RECYCLED_COLOR} strokeWidth="0" />

            {/* Fresh area - Now middle layer */}
            <path d={freshAreaPath} fill="url(#freshGradient)" stroke={FRESH_COLOR} strokeWidth="0" />

            {/* BPG area - Still top layer */}
            <path d={bpgAreaPath} fill="url(#bpgGradient)" stroke={BPG_COLOR} strokeWidth="0" />

            {/* Current epoch indicator line */}
            {currentEpoch && (() => {
              const currentEpochData = chartData.find((d) => d.epoch === currentEpoch.epochNumber)
              if (!currentEpochData) return null

              return (
                <line
                  x1={xScale(currentEpochData.epoch)}
                  y1={0}
                  x2={xScale(currentEpochData.epoch)}
                  y2={chartHeight}
                  stroke="white"
                  strokeOpacity="0.8"
                  strokeWidth="2"
                  strokeDasharray="4,4"
                />
              )
            })()}

            {/* Total emitted highlight line */}
            <path
              d={totalEmittedLine}
              fill="none"
              stroke={TOTAL_EMITTED_COLOR}
              strokeWidth={TOTAL_EMITTED_STROKE_WIDTH}
              strokeDasharray={TOTAL_EMITTED_STROKE_DASHARRAY}
            />

            {/* Hover epoch indicator line - connects mouse to data visually */}
            {hoveredEpoch &&
              (() => {
                const hoveredEpochX = xScale(hoveredEpoch)
                return (
                  <line
                    x1={hoveredEpochX}
                    y1={0}
                    x2={hoveredEpochX}
                    y2={chartHeight}
                    stroke="white"
                    strokeOpacity="0.6"
                    strokeWidth="1"
                    strokeDasharray="2,2"
                    style={{ pointerEvents: "none" }}
                  />
                )
              })()}

            {/* Invisible hover zones for each epoch */}
            {chartData.map((data, index) => {
              const epochWidth = chartWidth / (chartData.length - 1) // Space between epochs
              const centerX = xScale(data.epoch)
              const leftEdge = index === 0 ? 0 : centerX - epochWidth / 2
              const rightEdge = index === chartData.length - 1 ? chartWidth : centerX + epochWidth / 2
              const width = rightEdge - leftEdge

              return (
                <rect
                  key={data.epoch}
                  x={leftEdge}
                  y={0}
                  width={width}
                  height={chartHeight}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    setHoveredEpoch(data.epoch)
                    const rect = e.currentTarget.getBoundingClientRect()
                    const svgRect = rect // This is the chart area rect
                    setMousePosition({
                      x: e.clientX - svgRect.left,
                      y: e.clientY - svgRect.top,
                    })
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    const svgRect = rect
                    setMousePosition({
                      x: e.clientX - svgRect.left,
                      y: e.clientY - svgRect.top,
                    })
                  }}
                  onMouseLeave={() => {
                    setHoveredEpoch(null)
                    setMousePosition(null)
                  }}
                />
              )
            })}

            {/* X-axis */}
            <line
              x1={0}
              y1={chartHeight}
              x2={chartWidth}
              y2={chartHeight}
              stroke="currentColor"
              strokeOpacity="0.3"
              strokeWidth="2"
            />

            {/* Y-axis */}
            <line x1={0} y1={0} x2={0} y2={chartHeight} stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />

            {/* X-axis labels */}
            {xTicks.map((epoch) => {
              // Skip the first epoch if it's the minimum
              if (epoch === Math.min(...epochs)) return null

              return (
                <g key={epoch}>
                  <line
                    x1={xScale(epoch)}
                    y1={chartHeight}
                    x2={xScale(epoch)}
                    y2={chartHeight + 5}
                    stroke="currentColor"
                    strokeOpacity="0.3"
                    strokeWidth="2"
                  />
                  <text
                    x={xScale(epoch)}
                    y={chartHeight + 20}
                    textAnchor="middle"
                    className="fill-current text-xs text-t200"
                    fill="currentColor"
                  >
                    {epoch}
                  </text>
                </g>
              )
            })}

            {/* Y-axis labels */}
            {yTicks.map((tick) => (
              <g key={tick}>
                {/* Only render tick line if not zero */}
                {tick !== 0 && (
                  <line
                    x1={-5}
                    y1={yScale(tick)}
                    x2={0}
                    y2={yScale(tick)}
                    stroke="currentColor"
                    strokeOpacity="0.3"
                    strokeWidth="2"
                  />
                )}
                <text
                  x={-10}
                  y={yScale(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="fill-current text-sm text-t200"
                  fill="currentColor"
                >
                  {tick.toLocaleString()}
                </text>
              </g>
            ))}

            {/* Right Y-axis percentage labels at data boundaries */}
            {(() => {
              // Get the latest data point for percentage calculations
              const latestData = chartData[chartData.length - 1]
              if (!latestData) return null

              const recycledPercent = (latestData.recycled / latestData.totalEmitted) * 100
              const totalEmittedPercent = 100 // Always 100% by definition
              const bpgPercent = (latestData.bpg / latestData.totalEmitted) * 100

              return (
                <>
                  {/* Recycled percentage label */}
                  <g>
                    <line
                      x1={chartWidth}
                      y1={yScale(latestData.recycled)}
                      x2={chartWidth + 5}
                      y2={yScale(latestData.recycled)}
                      stroke="currentColor"
                      strokeOpacity="0.3"
                      strokeWidth="2"
                    />
                    <text
                      x={chartWidth + 10}
                      y={yScale(latestData.recycled)}
                      textAnchor="start"
                      dominantBaseline="middle"
                      className="fill-current text-sm text-t200"
                      fill="currentColor"
                    >
                      {recycledPercent.toFixed(1)}%
                    </text>
                  </g>

                  {/* Total emitted (100%) label */}
                  <g>
                    <line
                      x1={chartWidth}
                      y1={yScale(latestData.totalEmitted)}
                      x2={chartWidth + 5}
                      y2={yScale(latestData.totalEmitted)}
                      stroke="currentColor"
                      strokeOpacity="0.3"
                      strokeWidth="2"
                    />
                    <text
                      x={chartWidth + 10}
                      y={yScale(latestData.totalEmitted)}
                      textAnchor="start"
                      dominantBaseline="middle"
                      className="fill-current text-sm text-t200"
                      fill="currentColor"
                    >
                      100%
                    </text>
                  </g>

                  {/* BPG percentage label (above 100%) */}
                  {latestData.bpg > 0 &&
                    (() => {
                      const actualBpgPosition = yScale(latestData.totalEmitted + latestData.bpg)
                      const totalEmittedPosition = yScale(latestData.totalEmitted)
                      const minSafeTextPosition = totalEmittedPosition - 16 // 16px minimum distance
                      const textPosition = Math.min(actualBpgPosition, minSafeTextPosition)

                      return (
                        <g>
                          {/* Tick stays at actual data position */}
                          <line
                            x1={chartWidth}
                            y1={actualBpgPosition}
                            x2={chartWidth + 5}
                            y2={actualBpgPosition}
                            stroke="currentColor"
                            strokeOpacity="0.3"
                            strokeWidth="2"
                          />
                          {/* Text positioned safely above 100% label */}
                          <text
                            x={chartWidth + 10}
                            y={textPosition}
                            textAnchor="start"
                            dominantBaseline="middle"
                            className="fill-current text-sm text-t200"
                            fill="currentColor"
                          >
                            +{bpgPercent.toFixed(1)}%
                          </text>
                        </g>
                      )
                    })()}
                </>
              )
            })()}
          </g>

          {/* Horizontal Legend - Moved to top to avoid clash with epoch labels */}
          <g transform={`translate(${chartMargin.left + chartWidth / 2 - 180}, 12)`}>
            {/* Recycled - Now first to match bottom layer */}
            <circle cx="6" cy="6" r="6" fill={RECYCLED_COLOR} opacity="0.8" />
            <text x="18" y="11" className="fill-current text-sm text-t100" fill="currentColor">
              Recycled
            </text>

            {/* Fresh - Now second to match middle layer */}
            <circle cx="106" cy="6" r="6" fill={FRESH_COLOR} opacity="0.8" />
            <text x="118" y="11" className="fill-current text-sm text-t100" fill="currentColor">
              Fresh
            </text>

            {/* BPG */}
            <circle cx="186" cy="6" r="6" fill={BPG_COLOR} opacity="0.8" />
            <text x="198" y="11" className="fill-current text-sm text-t100" fill="currentColor">
              BPG
            </text>

            {/* Total Emitted - 12 wide , but 6 more on Total Emitted */}
            <line
              x1="254"
              y1="6"
              x2="266"
              y2="6"
              stroke={TOTAL_EMITTED_COLOR}
              strokeWidth={TOTAL_EMITTED_STROKE_WIDTH}
              strokeDasharray={TOTAL_EMITTED_STROKE_DASHARRAY}
            />
            <text x="272" y="11" className="fill-current text-sm text-t100" fill="currentColor">
              Total Emitted
            </text>
          </g>
        </svg>

        {/* Tooltip with D3-based positioning and smart edge detection */}
        {hoveredEpoch &&
          mousePosition &&
          (() => {
            const hoveredData = chartData.find((d) => d.epoch === hoveredEpoch)
            if (!hoveredData) return null

            // Tooltip dimensions
            const tooltipWidth = 200
            const tooltipHeight = 140
            const offset = 8 // Closer distance from cursor for better UX

            // Use D3 scale to get the epoch's actual X position in chart coordinates
            const epochXInChart = xScale(hoveredEpoch)
            const epochXInSvg = epochXInChart + chartMargin.left // Convert to SVG coordinates

            // Smart X positioning with edge detection
            let tooltipX: number
            const rightEdgeThreshold = svgWidth - tooltipWidth - offset
            const leftEdgeThreshold = offset

            if (epochXInSvg > rightEdgeThreshold) {
              // Near right edge: show tooltip to the left of epoch
              tooltipX = epochXInSvg - tooltipWidth - offset
            } else if (epochXInSvg < leftEdgeThreshold + tooltipWidth) {
              // Near left edge: show tooltip to the right of epoch
              tooltipX = epochXInSvg + offset
            } else {
              // Plenty of space: default to right of epoch
              tooltipX = epochXInSvg + offset
            }

            // Smart Y positioning with edge detection
            let tooltipY: number
            const mouseYInSvg = mousePosition.y // Already in SVG coordinates
            const topEdgeThreshold = tooltipHeight + offset
            const bottomEdgeThreshold = svgHeight - offset

            if (mouseYInSvg < topEdgeThreshold) {
              // Near top edge: show tooltip below cursor
              tooltipY = mouseYInSvg + offset
            } else if (mouseYInSvg > bottomEdgeThreshold) {
              // Near bottom edge: show tooltip above cursor
              tooltipY = mouseYInSvg - tooltipHeight - offset
            } else {
              // Default: show tooltip above cursor
              tooltipY = mouseYInSvg - tooltipHeight - offset
            }

            // Calculate percentages
            const recycledPercent = (hoveredData.recycled / hoveredData.totalEmitted) * 100
            const freshPercent = (hoveredData.fresh / hoveredData.totalEmitted) * 100
            const bpgPercent = (hoveredData.bpg / hoveredData.totalEmitted) * 100

            return (
              <div
                className="absolute pointer-events-none bg-p300 text-t100 text-sm rounded-lg p-3 shadow-lg border border-p300-light"
                style={{
                  left: tooltipX,
                  top: tooltipY,
                  width: tooltipWidth,
                }}
              >
                <div className="font-semibold mb-2 text-t100">Epoch {hoveredEpoch}</div>
                <div className="text-t200 mb-1 text-xs">{formatEpochDate(hoveredData.startTimestamp)}</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-t200">Recycled:</span>
                    <span className="text-t100">
                      {hoveredData.recycled.toLocaleString()} ({recycledPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-t200">Fresh:</span>
                    <span className="text-t100">
                      {hoveredData.fresh.toLocaleString()} ({freshPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-t200">BPG:</span>
                    <span className="text-t100">
                      {hoveredData.bpg.toLocaleString()} (+{bpgPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="border-t border-p300-light pt-1 mt-2">
                    <div className="flex justify-between font-medium text-t100">
                      <span>Total:</span>
                      <span>{hoveredData.totalEmitted.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })()}
      </div>
    )
  },
)
